/**
 * # AsNotificationHistory Component
 *
 * Displays a scrollable list of past notifications, newest first.
 * Reads from `commonAppData.notificationHistory` and re-renders on changes.
 *
 * ## Features
 * - Shows type indicator, message text, and timestamp for each entry
 * - Clear button to wipe the history
 * - Capped at MAX_STORED_NOTIFICATIONS entries (managed by as-notifications)
 */
import { liveAppData } from '../../store/store.ts';
import type { StoredNotification } from '../../data/data-interfaces.ts';
import styleText from './as-notification-history.css?raw';

export class AsNotificationHistory extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsNotificationHistory.sheet];
  }

  connectedCallback(): void {
    window.addEventListener('store-update', this.handleStoreUpdate);
    this.render();
  }

  disconnectedCallback(): void {
    window.removeEventListener('store-update', this.handleStoreUpdate);
  }

  private handleStoreUpdate = (event: Event): void => {
    if ((event as CustomEvent).detail?.prop === 'notificationHistory') {
      this.render();
    }
  };

  private clearHistory = (): void => {
    liveAppData.notificationHistory = [];
  };

  private formatTime(timestamp: number): string {
    const d = new Date(timestamp);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private render(): void {
    const history: StoredNotification[] = [...(liveAppData.notificationHistory ?? [])].reverse();

    if (history.length === 0) {
      this.shadowRoot!.innerHTML = '<p class="history-empty">No notifications yet.</p>';
      return;
    }

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="history-header">
        <span class="history-count">${history.length} notification${history.length === 1 ? '' : 's'}</span>
        <button class="clear-button">Clear</button>
      </div>
      <div class="history-list">
        ${history.map(n => /*html*/`
          <div class="history-entry">
            <div class="type-dot type-dot--${n.type}"></div>
            <div class="entry-body">
              <p class="entry-text">${this.escapeHtml(n.defaultText)}</p>
              <div class="entry-time">${this.formatTime(n.timestamp)}</div>
            </div>
          </div>
        `).join('')}
      </div>
    `;

    this.shadowRoot!.querySelector('.clear-button')?.addEventListener('click', this.clearHistory);
  }
}

if (!customElements.get('as-notification-history')) {
  customElements.define('as-notification-history', AsNotificationHistory);
} else {
  console.warn('as-notification-history already defined');
}
